import type { AxiosRequestConfig } from 'axios';
import { isAxiosError, isCancel } from 'axios';

import { isAxiosConfigTimeoutError } from './isAxiosConfigTimeoutError';
import {
  PromptStrategistHttpError,
  PromptStrategistTimeoutError
} from './promptStrategistErrors';
import {
  clampPromptStrategistIntent,
  type PromptStrategistJsonSchemaParams,
  type PromptStrategistJsonSchemaResponse,
  type PromptStrategistMcpServerDescriptionParams,
  type PromptStrategistMcpServerDescriptionResponse,
  type PromptStrategistMcpToolNameDescriptionParams,
  type PromptStrategistMcpToolNameDescriptionResponse,
  type PromptStrategistSystemPromptParams,
  type PromptStrategistSystemPromptResponse,
  type PromptStrategistUserPromptParams,
  type PromptStrategistUserPromptResponse
} from './types';

import axiosInstance, {
  DEFAULT_AXIOS_REQUEST_TIMEOUT_MS
} from '~/common/helpers/axiosInstance';

export {
  PromptStrategistHttpError,
  PromptStrategistTimeoutError
} from './promptStrategistErrors';

const PROMPT_STRATEGIST_PATH = 'ai-services/prompt-strategist';

function promptStrategistUrl(realm: string, endpoint: string): string {
  return `design/realms/${encodeURIComponent(realm)}/${PROMPT_STRATEGIST_PATH}/${endpoint}`;
}

function toOptionalTrimmed(value: string | undefined): string | undefined {
  if (value == null || value.trim() === '') return undefined;
  return value.trim();
}

function optionalTrimmedFields(
  fields: Record<string, string | undefined>
): Record<string, string> {
  return Object.entries(fields).reduce<Record<string, string>>(
    (acc, [key, value]) => {
      const trimmed = toOptionalTrimmed(value);
      if (trimmed !== undefined) {
        acc[key] = trimmed;
      }
      return acc;
    },
    {}
  );
}

function requestConfig(signal?: AbortSignal): AxiosRequestConfig {
  return signal ? { signal } : {};
}

function throwPromptStrategistPostError(error: unknown): never {
  if (error instanceof Error && isAxiosError(error)) {
    if (isCancel(error)) {
      throw error;
    }
    if (isAxiosConfigTimeoutError(error, DEFAULT_AXIOS_REQUEST_TIMEOUT_MS)) {
      throw new PromptStrategistTimeoutError();
    }
    throw new PromptStrategistHttpError(error.response?.status);
  }
  throw error;
}

async function promptStrategistPost<TResponse>(
  realm: string,
  endpoint: string,
  body: Record<string, string>,
  signal?: AbortSignal
): Promise<TResponse> {
  try {
    const { data } = await axiosInstance.post<TResponse>(
      promptStrategistUrl(realm, endpoint),
      body,
      requestConfig(signal)
    );
    return data;
  } catch (error) {
    return throwPromptStrategistPostError(error);
  }
}

export const promptStrategistSystemPrompt = async ({
  realm,
  provider,
  intent,
  initialSystemPrompt,
  tools,
  files,
  structuredOutput,
  signal
}: PromptStrategistSystemPromptParams): Promise<PromptStrategistSystemPromptResponse> => {
  const body: Record<string, string> = {
    provider,
    intent: clampPromptStrategistIntent(intent),
    ...optionalTrimmedFields({
      initialSystemPrompt,
      tools,
      files,
      structuredOutput
    })
  };

  return promptStrategistPost<PromptStrategistSystemPromptResponse>(
    realm,
    'system-prompt',
    body,
    signal
  );
};

export const promptStrategistUserPrompt = async ({
  realm,
  provider,
  intent,
  currentSystemPrompt,
  initialUserPrompt,
  signal
}: PromptStrategistUserPromptParams): Promise<PromptStrategistUserPromptResponse> => {
  const body: Record<string, string> = {
    provider,
    intent: clampPromptStrategistIntent(intent),
    currentSystemPrompt,
    ...optionalTrimmedFields({ initialUserPrompt })
  };

  return promptStrategistPost<PromptStrategistUserPromptResponse>(
    realm,
    'user-prompt',
    body,
    signal
  );
};

export const promptStrategistJsonSchema = async ({
  realm,
  intent,
  currentSchema,
  initialSystemPrompt,
  initialUserPrompt,
  signal
}: PromptStrategistJsonSchemaParams): Promise<PromptStrategistJsonSchemaResponse> => {
  const body: Record<string, string> = {
    intent: clampPromptStrategistIntent(intent),
    ...optionalTrimmedFields({
      currentSchema,
      initialSystemPrompt,
      initialUserPrompt
    })
  };

  return promptStrategistPost<PromptStrategistJsonSchemaResponse>(
    realm,
    'json-schema',
    body,
    signal
  );
};

export const promptStrategistMcpToolNameDescription = async ({
  realm,
  intent,
  toolIdOrName,
  currentName,
  currentDescription,
  flowSpec,
  signal
}: PromptStrategistMcpToolNameDescriptionParams): Promise<PromptStrategistMcpToolNameDescriptionResponse> => {
  const body: Record<string, string> = {
    intent: clampPromptStrategistIntent(intent),
    toolIdOrName: toolIdOrName.trim(),
    ...optionalTrimmedFields({
      currentName,
      currentDescription,
      flowSpec
    })
  };

  return promptStrategistPost<PromptStrategistMcpToolNameDescriptionResponse>(
    realm,
    'mcp-tool/name-description',
    body,
    signal
  );
};

export const promptStrategistMcpServerDescription = async ({
  realm,
  intent,
  currentName,
  currentDescription,
  triggerSpec,
  signal
}: PromptStrategistMcpServerDescriptionParams): Promise<PromptStrategistMcpServerDescriptionResponse> => {
  const body: Record<string, string> = {
    intent: clampPromptStrategistIntent(intent),
    ...optionalTrimmedFields({
      currentName,
      currentDescription,
      triggerSpec
    })
  };

  return promptStrategistPost<PromptStrategistMcpServerDescriptionResponse>(
    realm,
    'mcp-server-description',
    body,
    signal
  );
};
